import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Accordion,
    Box,
    Flex,
    Heading,
    Img,
    Text,
    useMediaQuery,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import CollapsibleSectionItem from './partials/collapsible-section-item'
import {
    createCompleteImageSet,
    getDamBaseUrl,
    getRatiosAndSOurcesFromImageObj,
} from '../../../custom-hooks/use-dam-image'
import useSlideUpAnimation from '../../../custom-hooks/use-slide-up-animation'

const IMAGE_BREAKPOINT_PRIORITY = ['desktop', 'tablet_1024', 'tablet_768', 'mobile']
const EASE_OUT_CSS = 'cubic-bezier(0.22, 1, 0.36, 1)'

const getPictureData = (image) => {
    if (!image) return null

    const imageGroup = createCompleteImageSet(image)
    const [, sources] = getRatiosAndSOurcesFromImageObj(imageGroup)

    const fallbackKey = IMAGE_BREAKPOINT_PRIORITY.find(
        (key) => imageGroup?.[key]?.rendition && imageGroup?.[key]?.dam_image?.[0]?.id,
    )

    if (!fallbackKey) return null

    const fallback = imageGroup[fallbackKey]
    const fallbackUrl = `${getDamBaseUrl()}/${fallback.rendition}/assets/${
        fallback.dam_image[0].id
    }`

    return {sources, fallbackUrl}
}

const FadePicture = ({pictureData, alt, isTop, onFadeInEnd}) => {
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const raf = requestAnimationFrame(() => setVisible(true))
        return () => cancelAnimationFrame(raf)
    }, [])

    return (
        <Box
            position="absolute"
            inset={0}
            opacity={visible ? 1 : 0}
            transform={visible ? 'scale(1)' : 'scale(1.04)'}
            transition={`opacity 0.5s ${EASE_OUT_CSS}, transform 0.6s ${EASE_OUT_CSS}`}
            onTransitionEnd={isTop ? onFadeInEnd : undefined}
        >
            <picture style={{display: 'block', width: '100%', height: '100%'}}>
                {pictureData.sources}
                <Img
                    src={pictureData.fallbackUrl}
                    alt={alt || ''}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    objectPosition="center"
                />
            </picture>
        </Box>
    )
}

FadePicture.propTypes = {
    pictureData: PropTypes.shape({
        sources: PropTypes.node,
        fallbackUrl: PropTypes.string,
    }).isRequired,
    alt: PropTypes.string,
    isTop: PropTypes.bool.isRequired,
    onFadeInEnd: PropTypes.func,
}

export const CollapsibleSection = ({sectionTitle, sectionSubtitle, accordions = []}) => {
    const [expandedIndex, setExpandedIndex] = useState(0)
    const [imageLayers, setImageLayers] = useState(() => [
        {image: accordions[0]?.image, title: accordions[0]?.title, key: 0},
    ])
    const nextLayerKey = useRef(1)
    const [isDesktop] = useMediaQuery('(min-width: 1280px)', {fallback: true})
    const styles = useMultiStyleConfig('CollapsibleSection', {})
    const {containerRef, getAnimProps} = useSlideUpAnimation()

    if (!accordions || accordions.length === 0) return null

    const changeImage = (item) => {
        setImageLayers((previousLayers) => {
            const lastLayerIndex = previousLayers.length - 1
            const previousLayer = previousLayers[lastLayerIndex]
            if (previousLayer?.image === item?.image) {
                if (previousLayer?.title === item?.title) return previousLayers

                return previousLayers.map((layer, index) =>
                    index === lastLayerIndex ? {...layer, title: item?.title} : layer,
                )
            }
            if (!item?.image) return []

            return [...previousLayers, {...item, key: nextLayerKey.current++}]
        })
    }

    const handleFadeInEnd = (key) => {
        setImageLayers((previousLayers) =>
            previousLayers.length > 1
                ? previousLayers.filter((layer) => layer.key === key)
                : previousLayers,
        )
    }

    const hasImage = imageLayers.some((layer) => getPictureData(layer.image))

    const renderImage = () => (
        <Box {...styles.image}>
            {imageLayers.map((layer, index) => {
                const pictureData = getPictureData(layer.image)
                if (!pictureData) return null

                return (
                    <FadePicture
                        key={layer.key}
                        pictureData={pictureData}
                        alt={layer.title}
                        isTop={index === imageLayers.length - 1}
                        onFadeInEnd={() => handleFadeInEnd(layer.key)}
                    />
                )
            })}
        </Box>
    )

    return (
        <Flex {...styles.outerContainer} data-testid="collapsible-section">
            <Flex {...styles.container}>
                <Flex {...(isDesktop ? styles.leftContainer : styles.mobileContainer)}>
                    {(sectionTitle || sectionSubtitle) && (
                        <Flex {...styles.headerGrid}>
                            {sectionTitle && <Heading {...styles.title}>{sectionTitle}</Heading>}
                            {sectionSubtitle && (
                                <Text {...styles.description}>{sectionSubtitle}</Text>
                            )}
                        </Flex>
                    )}
                    {!isDesktop && hasImage && renderImage()}
                    <Accordion
                        ref={containerRef}
                        index={expandedIndex}
                        onChange={(index) => {
                            setExpandedIndex(index)
                            if (index >= 0) changeImage(accordions[index])
                        }}
                        allowToggle
                        {...styles.benefitsContainer}
                    >
                        {accordions.map((item, index) => (
                            <Box key={item?._metadata?.uid || index} {...getAnimProps(index)}>
                                <CollapsibleSectionItem
                                    item={item}
                                    isExpanded={expandedIndex === index}
                                />
                            </Box>
                        ))}
                    </Accordion>
                </Flex>
                {isDesktop && hasImage && renderImage()}
            </Flex>
        </Flex>
    )
}

CollapsibleSection.propTypes = {
    sectionTitle: PropTypes.string,
    sectionSubtitle: PropTypes.string,
    accordions: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            subtitle: PropTypes.string,
            icon: PropTypes.shape({url: PropTypes.string}),
            image: PropTypes.object,
        }),
    ),
}

export default CollapsibleSection
